import { themeAtom } from "../../shared/atoms";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

type VarianceHelpPanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

const VarianceHelpPanel: React.FC<VarianceHelpPanelProps> = ({ isOpen, onClose }) => {
  const theme = useAtomValue(themeAtom);

  const helpSections = [
    {
      title: "Drivers",
      text: 'You can add one or several drivers to explain what caused the variance during the voyage. Each driver can include multiple PnL items. For example, a driver "change in speed instruction" may include variance on Bunkers and Hire.',
    },
    {
      title: "Revenues vs. Expenses",
      text: "The PnL items are aligned with IMOS representation. They are split in 2 categories, highlighted by the color coding: Green for the Revenue items; Red for the Expenses. This is important when explaining the PnL. In the Revenues, a positive $ amount will have a positive PnL impact. On the contrary, a positive amount in the expense category will lead to a negative PnL impact.",
    },
    {
      title: "Example 1 : increased Freight income of $ 20k",
      text: 'For this, you can enter a positive value on the "Freight" row, which will lead to a positive PnL impact of $ +20k. The unallocated amount will reduce by 20k.',
    },
    {
      title: "Example 2 : increased bunker consumption of $ 20k",
      text: 'For this, you can enter a positive value on the "Bunkers" row, which will lead to a negative PnL impact. This will be deducted from the unallocated, meaning a +20k will be added to the Unallocated total.',
    },
  ];

  return (
    <>
      <div
        className={twMerge(
          "fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ease-in-out",
          isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
      />

      <aside
        className={twMerge(
          "fixed right-0 top-0 z-50 flex h-full w-1/3 flex-col !rounded-none border-l shadow-2xl transform-gpu transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]",
          isOpen ? "translate-x-0" : "translate-x-full",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
          theme.uwhcAssistant.border,
        )}
        style={{ borderRadius: 0 }}
        aria-hidden={!isOpen}
      >
        <div
          className={twMerge(
            "flex w-full flex-none items-center justify-between !rounded-none px-8 py-6",
            theme.voyPnl.sidePanelHeaderText,
             theme.home.tailwind.greenBg
          )}
          style={{ borderRadius: 0 }}
        >
          <div className={twMerge("flex items-center gap-4")}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-current"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <path d="M12 17h.01"></path>
            </svg>
            <h2 className="text-[20px] font-bold leading-7 text-current">How to explain the PnL variance</h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close help panel"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-10 py-8">
          <div className="flex flex-col gap-6">
            {helpSections.map((section) => (
              <div key={section.title} className="flex flex-col gap-3">
                <h3 className={twMerge("text-[16px] font-bold leading-6", theme.home.tailwind.textDefault)}>
                  {section.title}
                </h3>
                <p className={twMerge("text-[14px] leading-[22px]", theme.uwhcAssistant.text)}>{section.text}</p>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
};

export default VarianceHelpPanel;
